import {
    AddPropertyIcon,
    PropertiesIcon,
    TeamIcon,
    WorkTypeIcon
} from "../../icons";
import {
    Box,
    Divider,
    List
} from "@mui/material";
import {
    eligibilityCheckErrorAtom,
    isLiveInspectionDrawerOpenAtom,
    isLogoutWarningPromptOpenAtom,
    showUserOnboardingWizardAtom,
    showVideoCallAssistantSelector,
    sidebarDrawerConfigurationsAtom
} from "../../../lib/sidebar/SidebarRecoilStates";
import {
    useRecoilValue,
    useSetRecoilState
} from "recoil";

import ContactIcon from "../../icons/ContactIcon";
import { DataSyncStatusIndicatorListItem } from "./DataSyncStatusIndicatorListItem";
import { DrawerListItemButton } from "../DrawerListItemButton";
import { IssueFilterDrawerListItem } from "../IssueFilterDrawerListItem";
import { LogoutIcon } from "../../icons";
import { OrganizationType } from "../../../models";
import SettingsIcon from "../../icons/SettingsIcon";
import { SidebarDrawerConfiguration } from "../../../lib/sidebar/SidebarDrawerConfiguration";
import { ValidationDrawerListItem } from "../ValidationDrawerListItem";
import VideoCallOutlinedIcon from '@mui/icons-material/VideoCallOutlined';
import { useTeamManagement } from "../../../pages/teamManagement/useTeamManagement";
import { useUser } from "../../user/UserContext";

type DefaultDrawerListItemsProps = {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
};

export const DefaultDrawerListItems = (props: DefaultDrawerListItemsProps) => {
    const { isOpen, setIsOpen } = props;
    const setLogoutWarningPromptOpen = useSetRecoilState(isLogoutWarningPromptOpenAtom);
    const sidebarDrawerConfiguration = useRecoilValue<SidebarDrawerConfiguration>(sidebarDrawerConfigurationsAtom);
    const eligibilityCheckError = useRecoilValue(eligibilityCheckErrorAtom);
    const showUserOnboardingWizard = useRecoilValue(showUserOnboardingWizardAtom);
    const showVideoCallAssistant = useRecoilValue<boolean>(showVideoCallAssistantSelector);
    const setIsLiveInspectionDrawerOpen = useSetRecoilState(isLiveInspectionDrawerOpenAtom);
    const { isAccountOwner } = useTeamManagement();
    const { accountType } = useUser();
    const canCreateProperty = accountType === OrganizationType.PROPERTY_MANAGEMENT || isAccountOwner;

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                height: "100%"
            }}
        >
            <Box>
                <List
                    disablePadding
                    sx={
                        showUserOnboardingWizard || eligibilityCheckError ? {
                            display: "none"
                        } : {
                            display: "block"
                        }
                    }
                >
                    <Divider />
                    <DrawerListItemButton
                        key="view-properties"
                        icon={<PropertiesIcon />}
                        title="View Properties"
                        isOpen={isOpen}
                        linkTo="/properties"
                        onClick={() => setIsOpen(false)}
                    />
                    <Divider />
                    <Box
                        pt={6}
                    />
                    <Divider />
                    {canCreateProperty && (
                        <>
                            <DrawerListItemButton
                                key="add-properties"
                                icon={<AddPropertyIcon />}
                                title="Add Property"
                                isOpen={isOpen}
                                linkTo="/properties/create"
                                onClick={() => setIsOpen(false)}
                            />
                            <Divider />
                        </>
                    )}
                    {isAccountOwner && (
                        <>
                            <DrawerListItemButton
                                key="team-management"
                                icon={<TeamIcon />}
                                title="Team Management"
                                isOpen={isOpen}
                                linkTo="/account/team"
                                onClick={() => setIsOpen(false)}
                            />
                            <Divider />
                        </>
                    )}
                    <DrawerListItemButton
                        key="worktypes"
                        icon={<WorkTypeIcon />}
                        title="Work Types"
                        isOpen={isOpen}
                        linkTo="/team"
                        onClick={() => setIsOpen(false)}
                    />
                    <Divider />
                    <DrawerListItemButton
                        key="contact"
                        icon={<ContactIcon />}
                        title="Contacts"
                        isOpen={isOpen}
                        linkTo="/contacts"
                        onClick={() => setIsOpen(false)}
                    />
                    {sidebarDrawerConfiguration.displayValidationButton && (
                        <>
                            <Divider />
                            <ValidationDrawerListItem
                                isOpen={isOpen}
                                setIsOpen={setIsOpen}
                            />
                        </>
                    )}
                    {sidebarDrawerConfiguration.displayIssueFilteringButton && (
                        <>
                            <Divider />
                            <IssueFilterDrawerListItem
                                isOpen={isOpen}
                                setIsOpen={setIsOpen}
                            />
                        </>
                    )}
                    <Divider />
                    <Box
                        pt={6}
                    />
                    {showVideoCallAssistant && (
                        <>
                            <Divider />
                            <DrawerListItemButton
                                onClick={() => {
                                    setIsOpen(false);
                                    setIsLiveInspectionDrawerOpen(true);
                                }}
                                icon={<VideoCallOutlinedIcon color='primary' />}
                                title={'Remote Inspection'}
                                isOpen={isOpen}
                            />
                            <Divider />
                        </>
                    )}
                </List>
            </Box>
            <Box>
                <DrawerListItemButton
                    key="settings"
                    icon={<SettingsIcon />}
                    title="Settings"
                    isOpen={isOpen}
                    linkTo="/settings"
                    onClick={() => setIsOpen(false)}
                />
                <Divider />
                {isOpen && (
                    <>
                        <DrawerListItemButton
                            key="logout"
                            icon={<LogoutIcon />}
                            title="Log Out"
                            isOpen={isOpen}
                            onClick={() => setLogoutWarningPromptOpen(true)}
                        />
                        <Divider />
                    </>
                )}
                <DataSyncStatusIndicatorListItem isOpen={isOpen} />
            </Box>
        </Box>
    );
};